import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, Plus, Pencil, Trash2, ArrowRight, Check, X, AlertTriangle, Building2, Sparkles,
} from 'lucide-react';
import Layout from '../components/Layout';
import { Card, PageHeader, Button, Badge, EmptyState, Spinner, ErrorMessage } from '../components/ui';
import { workspacesApi } from '../lib/api';
import type { Workspace, WorkspacesInfo } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBilling } from '../lib/billing';
import { switchWorkspaceAndReload } from '../lib/plans';

export default function Workspaces() {
  const { user } = useAuth();
  const { billing, loading: billingLoading, isAgency } = useBilling();
  const [info, setInfo] = useState<WorkspacesInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<Workspace | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const [switchingId, setSwitchingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await workspacesApi.list();
      if (res.success && res.data) setInfo(res.data);
      else setError(res.message || 'Failed to load workspaces');
    } catch {
      setError('Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const inWorkspace = !!user?.actor;
  const enabled = info?.enabled ?? isAgency;
  const count = info?.workspaces.length ?? 0;
  const limit = info?.limit ?? 10;
  const atLimit = count >= limit;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const res = await workspacesApi.create(name);
      if (res.success) {
        setShowCreate(false);
        setNewName('');
        await load();
      } else {
        setShowCreate(false);
        setError(res.message || 'Failed to create workspace');
      }
    } catch {
      setError('Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    setError(null);
    try {
      const res = await workspacesApi.rename(id, name);
      if (res.success) {
        setInfo((prev) => prev ? { ...prev, workspaces: prev.workspaces.map((w) => (w.id === id ? { ...w, name } : w)) } : prev);
        setEditingId(null);
      } else {
        setError(res.message || 'Failed to rename workspace');
      }
    } catch {
      setError('Failed to rename workspace');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await workspacesApi.remove(deleteTarget.id);
      if (res.success) {
        setInfo((prev) => prev ? { ...prev, workspaces: prev.workspaces.filter((w) => w.id !== deleteTarget.id) } : prev);
        setDeleteTarget(null);
        setDeleteConfirm('');
      } else {
        setError(res.message || 'Failed to delete workspace');
      }
    } catch {
      setError('Failed to delete workspace');
    } finally {
      setDeleting(false);
    }
  };

  const handleOpen = async (id: string) => {
    setSwitchingId(id);
    setError(null);
    const err = await switchWorkspaceAndReload(id, '/dashboard');
    if (err) { setError(err); setSwitchingId(null); }
  };

  if (loading || (billingLoading && !billing)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64"><Spinner /></div>
      </Layout>
    );
  }

  // Not on Agency: upsell.
  if (!enabled && !inWorkspace) {
    return (
      <Layout>
        <EmptyState
          icon={Building2}
          title="Client workspaces are part of Agency"
          description="Agency ($197/mo) includes 10 fully separated client workspaces — each with its own connections, forms, quizzes, backups and API keys — all managed from one login."
          action={<Link to="/billing" className="ds-btn ds-btn-primary"><Sparkles className="w-4 h-4" /> Upgrade to Agency</Link>}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto animate-fade">
        <PageHeader
          eyebrow="Agency"
          title="Client Workspaces"
          description="Each workspace is fully separated, like its own account. Switch between them from the sidebar."
          actions={!inWorkspace ? (
            <Button icon={Plus} onClick={() => setShowCreate(true)} disabled={atLimit}>
              New workspace
            </Button>
          ) : undefined}
        />

        {error && (
          <div className="mb-4 p-3 bg-danger-soft border border-line rounded-[11px] flex items-center gap-2 text-danger text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" /> <ErrorMessage message={error} />
            <button onClick={() => setError(null)} className="ml-auto text-ink-3 hover:text-danger"><X className="w-4 h-4" /></button>
          </div>
        )}

        {inWorkspace && (
          <div className="mb-4 p-3 bg-surface-2 border border-line rounded-[11px] text-ink-2 text-sm font-medium">
            You're working inside a client workspace. Switch back to the agency account to create, rename or delete workspaces.
          </div>
        )}

        <div className="flex items-center gap-2 mb-3">
          <span className="font-semibold text-[13px] text-ink-2">Workspaces used</span>
          <Badge tone={atLimit ? 'warning' : 'neutral'}>{count} / {limit}</Badge>
        </div>

        {count === 0 ? (
          <Card padded className="text-center py-12">
            <div className="w-14 h-14 mx-auto rounded-[16px] bg-accent-soft text-accent-ink flex items-center justify-center">
              <Briefcase className="w-6 h-6" />
            </div>
            <h3 className="mt-4 font-display font-bold text-[18px] text-ink">No client workspaces yet</h3>
            <p className="mt-1.5 text-[13px] text-ink-2 font-medium">Create one for each client to keep their data separate.</p>
            {!inWorkspace && (
              <Button icon={Plus} className="mt-4" onClick={() => setShowCreate(true)}>New workspace</Button>
            )}
          </Card>
        ) : (
          <Card className="divide-y divide-line">
            {info!.workspaces.map((ws) => {
              const current = user?.workspace?.id === ws.id;
              return (
                <div key={ws.id} className="flex items-center gap-3 px-5 py-3.5 flex-wrap">
                  <span className="w-9 h-9 rounded-[10px] bg-accent-soft text-accent-ink flex items-center justify-center flex-shrink-0">
                    <Briefcase className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    {editingId === ws.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRename(ws.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          autoFocus
                          className="ds-input max-w-xs"
                        />
                        <button onClick={() => handleRename(ws.id)} className="p-2 rounded-lg text-success hover:bg-success-soft" title="Save"><Check className="w-4 h-4" /></button>
                        <button onClick={() => setEditingId(null)} className="p-2 rounded-lg text-ink-3 hover:bg-surface-2" title="Cancel"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[14px] text-ink truncate">{ws.name}</span>
                          {current && <Badge tone="accent" dot>Current</Badge>}
                        </div>
                        {ws.createdAt > 0 && (
                          <div className="text-[12px] text-ink-3 font-medium">Created {new Date(ws.createdAt).toLocaleDateString()}</div>
                        )}
                      </>
                    )}
                  </div>
                  {editingId !== ws.id && (
                    <div className="flex items-center gap-1.5">
                      {!current && (
                        <Button variant="ghost" icon={ArrowRight} onClick={() => handleOpen(ws.id)} disabled={switchingId !== null}>
                          {switchingId === ws.id ? 'Opening…' : 'Open'}
                        </Button>
                      )}
                      {!inWorkspace && (
                        <>
                          <button
                            onClick={() => { setEditingId(ws.id); setEditName(ws.name); }}
                            title="Rename"
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-ink-3 hover:text-ink hover:bg-surface-2 transition"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setDeleteTarget(ws); setDeleteConfirm(''); }}
                            title="Delete"
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-ink-3 hover:text-danger hover:bg-danger-soft transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        )}

        {/* Create modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <h2 className="font-display text-lg font-bold text-ink mb-4">New client workspace</h2>
              <label className="ds-label">Workspace name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                placeholder="Acme Co"
                autoFocus
                className="ds-input"
              />
              <p className="text-xs text-ink-3 mt-2">{count} of {limit} workspaces used.</p>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !newName.trim()}>
                  {creating ? 'Creating...' : 'Create workspace'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirm modal */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <div className="flex items-center gap-2 text-danger">
                <AlertTriangle className="w-5 h-5" />
                <h2 className="font-display text-lg font-bold">Delete "{deleteTarget.name}"?</h2>
              </div>
              <p className="text-[13px] text-ink-2 mt-3 leading-relaxed">
                This permanently deletes the workspace and <strong>all of its data</strong>: platform connections, forms,
                quizzes, buttons, popups, webhooks, submissions, backups, API keys and Whop links. Live embeds for this
                workspace will stop working. This cannot be undone.
              </p>
              <label className="ds-label mt-4">Type the workspace name to confirm</label>
              <input
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={deleteTarget.name}
                autoFocus
                className="ds-input"
              />
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                <Button
                  variant="danger"
                  icon={Trash2}
                  onClick={handleDelete}
                  disabled={deleting || deleteConfirm.trim() !== deleteTarget.name.trim()}
                >
                  {deleting ? 'Deleting...' : 'Delete workspace'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
